import React from 'react';
import Select, { SingleValue, components, StylesConfig } from 'react-select';
import styled from 'styled-components';
import noImageAvailable from "../assets/no_image.png";
import { getImagePath } from '../utils/utils';

// Define the type for options
interface ItemOption {
  value: string;
  label: string;
  imageUrl: string;
}

// Styled component for the image
const Image = styled.img`
  width: 40px;
  height: 40px;
  margin-right: 10px;
  vertical-align: middle;
    object-fit: contain; /* Ensures the entire image is visible within the container while maintaining its aspect ratio */
  background-color: #f0f0f0; /* Light background color */
  display: block; /* Ensures the image takes up the container space properly */

`;

// Utility function to determine if the URL is a Base64 string or an image URL
const getImageSrc = (imageUrl: string) => {
  const isImage = /\.(gif|jpe?g|tiff?|png|webp|bmp)$/i.test(imageUrl);
  return isImage ? getImagePath(imageUrl) : noImageAvailable;
};

// Custom SingleValue component
const SingleValueComponent = (props: any) => {
  const imageSrc = getImageSrc(props.data.imageUrl);
  return (
    <components.SingleValue {...props}>
      <Image src={imageSrc} alt={props.data.label} />
      {props.data.label}
    </components.SingleValue>
  );
};

// Custom Option component
const OptionComponent = (props: any) => {
  const imageSrc = getImageSrc(props.data.imageUrl);
  return (
    <components.Option {...props}>
      <Image src={imageSrc} alt={props.data.label} />
      {props.data.label}
    </components.Option>
  );
};

// Define props for the generic ItemSelect component
interface ItemSelectProps<T> {
  items: T[];
  selectedItemName: string | null;
  index: number;
  handleItemSelect: (index: number, value: string | null) => void;
  disabled?: boolean;
  mapToOption: (item: T) => ItemOption; // Mapping function to convert items to ItemOption
}

// Main ItemSelect component with generic type T
const ItemSelect = <T,>({
  items,
  selectedItemName,
  index,
  handleItemSelect,
  disabled = false,
  mapToOption,
}: ItemSelectProps<T>) => {
  // Map items to ItemOption format using the provided mapping function
  const options: ItemOption[] = items.map(mapToOption);

  // Find the selected option based on selectedItemName
  const selectedOption = options.find(option => option.value === selectedItemName);

  // Custom styles for react-select
  const customStyles: StylesConfig<ItemOption, false> = {
    control: (provided) => ({
      ...provided,
      borderColor: '#ccc',
      opacity: disabled ? 0.5 : 1,
      pointerEvents: disabled ? 'none' : 'auto',
    }),
    option: (provided) => ({
      ...provided,
      display: 'flex',
      alignItems: 'center',
    }),
    singleValue: (provided) => ({
      ...provided,
      display: 'flex',
      alignItems: 'center',
    }),
  };

  return (
    <Select
      value={selectedOption}
      onChange={(e: SingleValue<ItemOption>) => {
        handleItemSelect(index, e ? e.value : null);
      }}
      options={options}
      placeholder="Search and select an item..."
      isClearable
      noOptionsMessage={() => 'No options available. Please search.'}
      components={{ SingleValue: SingleValueComponent, Option: OptionComponent }}
      styles={customStyles}
    />
  );
};

export default ItemSelect;
